import React from 'react'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { deregisterBodyScrollLock, registerBodyScrollLock } from '@mobi/utils/functions'
import * as EventAggregator from '@classic/AppUtils/Framework/Messaging/EventAggregator'
import { GiddyUpModalCloseTopic } from '@classic/Betting-v2/Components/Pages/Starters/constants'
import { MainContainer } from '@core/Components/Containers'
import { OverlayClose } from '@core/Components/Overlay'
import {
  OverlayCloseButtonStyled,
  OverlayHeaderStyled,
  OverlayHeadingStyled,
} from '@core/Components/Overlay/Overlay.styles'
import {
  updateGiddyUpQuaddieValueSelectedOnDataLayer,
  updateGiddyUpTabOnDataLayer,
} from '@core/Services/Analytics/helpers/dataLayer/updaters'
import { GiddyUp } from './Components/GiddyUp'
import { GiddyUpQuaddieTips } from './data-access/giddyUpQuaddieTips'
import { GiddyUpRaces } from './data-access/giddyUpRaces'
import { trackGiddyUpClosed, trackGiddyUpOpen } from './helpers/analytics'
import {
  ClearSelectionFunc,
  RaceDetailFunc,
  SelectAcceptorFunc,
  SelectQuaddieFunc,
  clearGetAcceptorFunc,
  clearSelection,
  setClearSelection,
  setGetAcceptorFunc,
  setGetRaceDetailFunc,
  setSelectAcceptorFunc,
  setSelectQuaddieFunc,
} from './integration'
import { deregisterSideEffects, registerSideEffects } from './side-effects'
import { ViewType, page$ } from './states/page'
import { quaddie$ } from './states/quaddie'
import { Initialise, Teardown } from './states/signals'
import type { AcceptorFunc } from './types'

interface IRaceInformation {
  raceKey: string
  fixtureKey: string
  getAcceptor: AcceptorFunc
  getRaceDetail: RaceDetailFunc
  selectAcceptor: SelectAcceptorFunc
  clearSelection: ClearSelectionFunc
  selectQuaddie: SelectQuaddieFunc
}

export const GiddyUpPage: React.FC<{ raceInfo: IRaceInformation }> = ({ raceInfo }) => {
  const containerElementRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    registerSideEffects()
    setGetAcceptorFunc(raceInfo.getAcceptor)
    setGetRaceDetailFunc(raceInfo.getRaceDetail)
    setSelectAcceptorFunc(raceInfo.selectAcceptor)
    setClearSelection(raceInfo.clearSelection)
    setSelectQuaddieFunc(raceInfo.selectQuaddie)

    const promises: Promise<unknown>[] = [GiddyUpRaces.getPromise(raceInfo.raceKey)]
    if (raceInfo.fixtureKey) promises.push(GiddyUpQuaddieTips.getPromise(raceInfo.fixtureKey))

    Promise.all(promises)
    Initialise({ raceKey: raceInfo.raceKey })
    trackGiddyUpOpen()

    return () => {
      EventAggregator.publish(GiddyUpModalCloseTopic)
      page$.first().subscribe(lastState => {
        if (lastState && lastState.acceptorSelected) clearSelection()
      })
      deregisterSideEffects()
      clearGetAcceptorFunc()
      Teardown()
      trackGiddyUpClosed()
    }
  }, [raceInfo])

  React.useEffect(() => {
    if (!containerElementRef.current) return
    const containerElement = containerElementRef.current
    registerBodyScrollLock(containerElement)
    return () => deregisterBodyScrollLock(containerElement)
  }, [])

  React.useEffect(() => {
    const tabChangeSubscription = page$
      .map<ISelectedTab>(x => ({ currentView: x ? ViewType[x.currentView] : ViewType[0] }))
      .distinctUntilChanged()
      .subscribe(record => updateGiddyUpTabOnDataLayer(record.currentView))

    const quaddieChangeSubscription = quaddie$
      .distinctUntilChanged()
      .subscribe(record => updateGiddyUpQuaddieValueSelectedOnDataLayer(record.valueSelected))

    return () => {
      tabChangeSubscription.dispose()
      quaddieChangeSubscription.dispose()
    }
  }, [])

  return (
    <div ref={containerElementRef} style={{ overflowY: 'auto' }}>
      <OverlayHeaderStyled>
        <OverlayHeadingStyled>Giddy-Up</OverlayHeadingStyled>
        <OverlayCloseButtonStyled data-testid='close-button' onClick={OverlayClose}>
          <Icon type='cross' title='Close' />
        </OverlayCloseButtonStyled>
      </OverlayHeaderStyled>

      <MainContainer background='grey'>
        <GiddyUp raceKey={raceInfo.raceKey} fixtureKey={raceInfo.fixtureKey} />
      </MainContainer>
    </div>
  )
}

// =====
// Types
// =====

interface ISelectedTab {
  currentView: string
}
